'use client';

import { useState } from 'react';
import { CareerPath } from '@/lib/careerPaths';
import { getNetworkingData, NetworkingScript } from '@/lib/networkingScriptsData';
import styles from './NetworkingScripts.module.css';

interface NetworkingScriptsProps {
  career: CareerPath;
}

type TabId = 'connect' | 'interview' | 'follow-up' | 'strategy';

export default function NetworkingScripts({ career }: NetworkingScriptsProps) {
  const [activeTab, setActiveTab] = useState<TabId>('connect');
  const [expandedScript, setExpandedScript] = useState<number | null>(0);
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null);

  const data = getNetworkingData(career);

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(id);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const tabs: { id: TabId; label: string; icon: string }[] = [
    { id: 'connect', label: 'Connection Requests', icon: '🤝' },
    { id: 'interview', label: 'Info Interviews', icon: '☕' },
    { id: 'follow-up', label: 'Follow-Up Scripts', icon: '📬' },
    { id: 'strategy', label: 'Strategy & Tips', icon: '💡' },
  ];

  const renderScriptCard = (script: NetworkingScript, index: number, prefix: string) => {
    const isExpanded = expandedScript === index;
    const copyId = `${prefix}-${index}`;

    return (
      <div
        key={index}
        className={`${styles.scriptCard} ${isExpanded ? styles.expanded : ''}`}
      >
        <button
          className={styles.scriptHeader}
          onClick={() => setExpandedScript(isExpanded ? null : index)}
        >
          <div className={styles.scriptInfo}>
            <h4>{script.scenario}</h4>
            <p>{script.description}</p>
          </div>
          <span className={styles.expandIcon}>{isExpanded ? '−' : '+'}</span>
        </button>

        {isExpanded && (
          <div className={styles.scriptContent}>
            <div className={styles.templateBox}>
              <div className={styles.templateHeader}>
                <span>Template Message</span>
                <button
                  className={styles.copyButton}
                  onClick={() => handleCopy(script.template, copyId)}
                >
                  {copiedIndex === copyId ? '✓ Copied!' : 'Copy'}
                </button>
              </div>
              <pre className={styles.templateText}>{script.template}</pre>
            </div>

            <div className={styles.customizationTips}>
              <h5>Customization Tips</h5>
              <ul>
                {script.customizationTips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>

            {script.followUpScript && (
              <div className={styles.followUpBox}>
                <div className={styles.templateHeader}>
                  <span>Follow-Up Template</span>
                  <button
                    className={styles.copyButton}
                    onClick={() => handleCopy(script.followUpScript!, `${copyId}-followup`)}
                  >
                    {copiedIndex === `${copyId}-followup` ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className={styles.templateText}>{script.followUpScript}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Networking Scripts</h3>
        <p className={styles.subtitle}>
          LinkedIn outreach templates for {career.title} professionals
        </p>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabNav}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              setExpandedScript(0);
            }}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.content}>
        {/* Connection Requests Tab */}
        {activeTab === 'connect' && (
          <div className={styles.scriptsTab}>
            <p className={styles.tabIntro}>
              Use these templates to send personalized connection requests.
              Remember: keep it short and personalize for each person.
            </p>
            <div className={styles.scriptsList}>
              {data.connectionRequests.map((script, i) =>
                renderScriptCard(script, i, 'connect')
              )}
            </div>
          </div>
        )}

        {/* Informational Interviews Tab */}
        {activeTab === 'interview' && (
          <div className={styles.scriptsTab}>
            <p className={styles.tabIntro}>
              Informational interviews are the #1 networking hack. Use these templates
              to request valuable conversations with professionals in your field.
            </p>
            <div className={styles.scriptsList}>
              {data.informationalInterviews.map((script, i) =>
                renderScriptCard(script, i, 'interview')
              )}
            </div>

            <div className={styles.infoBox}>
              <h4>Informational Interview Best Practices</h4>
              <ul>
                <li>Keep the ask to 15-20 minutes</li>
                <li>Come with 3-5 prepared questions</li>
                <li>Do research on the person beforehand</li>
                <li>Send a thank-you note within 24 hours</li>
                <li>Follow up with progress updates later</li>
              </ul>
            </div>
          </div>
        )}

        {/* Follow-Up Tab */}
        {activeTab === 'follow-up' && (
          <div className={styles.scriptsTab}>
            <p className={styles.tabIntro}>
              The follow-up is where relationships are built. Use these templates
              to stay in touch and show appreciation.
            </p>
            <div className={styles.scriptsList}>
              {data.followUpMessages.map((script, i) =>
                renderScriptCard(script, i, 'follow-up')
              )}
            </div>

            {data.followUpMessages.length === 0 && (
              <div className={styles.emptyState}>
                <p>Check the Informational Interviews tab for follow-up templates!</p>
              </div>
            )}
          </div>
        )}

        {/* Strategy Tab */}
        {activeTab === 'strategy' && (
          <div className={styles.strategyTab}>
            {/* Target People */}
            <div className={styles.section}>
              <h4>Who to Network With</h4>
              <div className={styles.targetPeopleList}>
                {data.targetPeople.map((person, i) => (
                  <div key={i} className={styles.targetPerson}>
                    <span className={styles.targetRole}>{person.role}</span>
                    <span className={styles.targetWhy}>{person.why}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className={styles.section}>
              <h4>Pro Tips</h4>
              <div className={styles.tipsGrid}>
                {data.tips.map((tip, i) => (
                  <div key={i} className={styles.tipCard} data-category={tip.category}>
                    <span className={styles.tipCategory}>{tip.category}</span>
                    <h5>{tip.title}</h5>
                    <p>{tip.content}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Do and Don't */}
            <div className={styles.doAndDontSection}>
              <div className={styles.doColumn}>
                <h4><span className={styles.checkIcon}>✓</span> Do</h4>
                <ul>
                  {data.doAndDont.do.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className={styles.dontColumn}>
                <h4><span className={styles.xIcon}>✗</span> Don&apos;t</h4>
                <ul>
                  {data.doAndDont.dont.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
